// Tenantra Inc. - Public page where a deleted tenant restores their account via a landlord link.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TenantReactivatePage() {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [state, setState] = useState('checking'); // checking | ready | invalid | done
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState('invalid'); setError('This reactivation link is missing its token.'); return; }
    axios.get(`${API}/tenant-portal/auth/reactivate/validate`, { params: { token } })
      .then((r) => { setInfo(r.data); setState('ready'); })
      .catch((e) => { setState('invalid'); setError(e.response?.data?.detail || 'This reactivation link is invalid.'); });
  }, [token]);

  const submit = async () => {
    if (pw.length < 8) { setError('Use at least 8 characters.'); return; }
    if (pw !== confirm) { setError('Passwords do not match.'); return; }
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/tenant-portal/auth/reactivate`, { token, new_password: pw });
      setState('done');
    } catch (e) {
      setError(e.response?.data?.detail || 'We could not reactivate your account.');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="tenant-reactivate-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-7 shadow-sm">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-9 h-9 rounded-lg bg-teal-600 flex items-center justify-center text-white font-bold">T</div>
          <span className="text-lg font-bold text-slate-900">Tenantra</span>
        </div>

        {state === 'checking' && (
          <div className="py-10 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />Checking your link</div>
        )}

        {state === 'invalid' && (
          <div className="py-6 text-center">
            <p className="text-sm font-semibold text-slate-800 mb-1">Link not valid</p>
            <p className="text-sm text-slate-500">{error}</p>
          </div>
        )}

        {state === 'ready' && (
          <div className="space-y-4">
            <div>
              <h1 className="text-xl font-bold text-slate-900">Welcome back</h1>
              <p className="text-sm text-slate-500 mt-1">
                {info?.full_name ? `${info.full_name}, your` : 'Your'} profile and verified ID are still on file, so there is no onboarding to redo. Set a new password to get back in.
              </p>
            </div>
            <div className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
              <ShieldCheck className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
              <span>Your ID verification stays valid. You will not be asked to verify your identity again.</span>
            </div>
            <div className="space-y-3">
              <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password"
                className="w-full h-11 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" data-testid="reactivate-password" />
              <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password"
                className="w-full h-11 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400" data-testid="reactivate-confirm" />
            </div>
            {error ? <p className="text-sm text-red-600" data-testid="reactivate-error">{error}</p> : null}
            <button onClick={submit} disabled={busy}
              className="w-full h-11 rounded-lg bg-slate-900 hover:bg-black text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="reactivate-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Reactivate my account
            </button>
          </div>
        )}

        {state === 'done' && (
          <div className="py-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <p className="text-base font-semibold text-slate-800">Your account is active again</p>
            <p className="text-sm text-slate-500 mt-1">Open the Tenantra app and sign in with your new password.</p>
          </div>
        )}
      </div>
    </div>
  );
}
